import { errorText } from '../errorText'
/**
 * Общие кусочки create-форм ЭДО (были в app/(portal)/processes.tsx).
 */
import type { ReactNode } from 'react'
import { Platform } from 'react-native'
import { Text, XStack, YStack } from 'tamagui'
import type { UseMutationResult } from '@tanstack/react-query'

import { useApp, type Localized } from '../../../i18n'
import { tone } from '../../../theme/tones'
import { Button, DateField } from '../../../ui'

/**
 * Field label + control. `required` marks a mandatory field with a red asterisk;
 * pair it with the control's `highlight` prop (DateField / TimeField) so an empty
 * required field carries the navy accent until it's filled. Red stays reserved
 * for real validation errors.
 */
export function Field({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <YStack gap={6}>
      <Text fontSize={12} fontWeight="600" color="$muted">
        {label}
        {required ? (
          <Text fontWeight="700" color={tone('red').text}>
            {' *'}
          </Text>
        ) : null}
      </Text>
      {children}
    </YStack>
  )
}

/** Общие подписи для подсказки «Чтобы отправить, заполните: …» (`FormActions` → `missing`). */
export const MISSING_WORKPLACE: Localized = { ru: 'Место работы', en: 'Workplace', az: 'İş yeri' }
export const MISSING_DATE_FROM: Localized = { ru: 'Дата «С»', en: 'Start date', az: 'Başlanğıc tarixi' }
export const MISSING_DATE_TO: Localized = { ru: 'Дата «По»', en: 'End date', az: 'Bitmə tarixi' }

// Date/time create-form fields stay compact on web; on mobile they grow to fill the row.
export const isWeb = Platform.OS === 'web'
export const dateCol: { width?: number; flex?: number; minWidth?: number } = isWeb ? { width: 160 } : { flex: 1, minWidth: 150 }
export const timeCol: { width?: number; flex?: number; minWidth?: number } = isWeb ? { width: 120 } : { flex: 1, minWidth: 96 }

/** Поле дат «С … По» формы отпуска. `toRequired={false}` — для бессрочного ухода за ребёнком. */
export function DateRange({
  from,
  to,
  setFrom,
  setTo,
  toRequired = true,
  toLabel,
}: {
  from: string
  to: string
  setFrom: (v: string) => void
  setTo: (v: string) => void
  toRequired?: boolean
  toLabel?: string
}) {
  const { t } = useApp()
  return (
    <XStack gap={12} flexWrap="wrap">
      <YStack {...dateCol} gap={6}>
        <Field label={t({ ru: 'С', en: 'From' })} required>
          <DateField
            value={from}
            onChange={(d) => {
              setFrom(d)
              if (to && to < d) setTo('')
            }}
            placeholder="ГГГГ-ММ-ДД"
            highlight={!from}
          />
        </Field>
      </YStack>
      <YStack {...dateCol} gap={6}>
        <Field label={toLabel ?? t({ ru: 'По', en: 'To' })} required={toRequired}>
          <DateField
            value={to}
            onChange={setTo}
            min={from || undefined}
            placeholder="ГГГГ-ММ-ДД"
            highlight={toRequired && !to}
          />
        </Field>
      </YStack>
    </XStack>
  )
}

/**
 * Кнопки формы + строка ошибки отправки. Состояние берём из самой мутации (`m`), а не
 * четырьмя пропсами: ошибка бэка показывается везде одинаково (errorText).
 * Дженерики не важны — читаем только три поля, общих для любого UseMutationResult.
 * `missing` — уже переведённые подписи незаполненных обязательных полей: объясняют,
 * почему кнопка неактивна (иначе серая кнопка без причины — «форма не отправляется»).
 * `extra` — кнопка между «Отменой» и главной (черновик); `submitLabel` — подпись главной.
 */
export function FormActions({
  m,
  ok,
  onCancel,
  onSubmit,
  missing,
  extra,
  submitLabel,
}: {
  m: Pick<UseMutationResult<unknown, Error, unknown, unknown>, 'isPending' | 'isError' | 'error'>
  ok: boolean
  onCancel: () => void
  onSubmit: () => void
  missing?: string[]
  extra?: ReactNode
  submitLabel?: string
}) {
  const { t } = useApp()
  return (
    <YStack gap={8}>
      {m.isError && (
        <Text fontSize={12} color={tone('red').text}>
          {t(errorText(m.error))}
        </Text>
      )}
      {!ok && !!missing?.length && (
        <Text fontSize={12} color="$muted">
          {t({ ru: 'Чтобы отправить, заполните: ', en: 'To submit, fill in: ', az: 'Göndərmək üçün doldurun: ' }) + missing.join(', ')}
        </Text>
      )}
      <XStack gap={10} justifyContent="flex-end" flexWrap="wrap">
        <Button onPress={onCancel}>{t({ ru: 'Отмена', en: 'Cancel' })}</Button>
        {extra}
        {/* disabled, а не только opacity: иначе кнопка кликается и активируется с клавиатуры,
            а submit молча ничего не делает. Курсор/aria-disabled берёт на себя ui Button. */}
        <Button variant="primary" onPress={onSubmit} disabled={!ok || m.isPending} opacity={ok ? 1 : 0.5}>
          {m.isPending ? t({ ru: 'Отправка…', en: 'Submitting…' }) : submitLabel ?? t({ ru: 'Отправить', en: 'Submit' })}
        </Button>
      </XStack>
    </YStack>
  )
}
